import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export enum PendingTransactionCheckStage {
    'wait',
    'first',
    'second',
    'third',
}

export interface PendingTransactionData {
    meterName: string
    amount: number
    status: string
    transactionId: string
    createdAt: Date
    updatedAt: Date
    stage: PendingTransactionCheckStage
    countdown: number // New field for countdown
}

export const PENDING_TRANSACTION_VALIDITY_PERIOD = 15 // minutes

export const PENDING_TRANSACTION_MULTIPLE_CHECK_DURATION = 5 // minutes

interface TransactionsState {
    pendingTransactions: { [key: string]: PendingTransactionData }
}

const initialState: TransactionsState = {
    pendingTransactions: {},
}

export const transactionsSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {
        addPendingTransaction: (state, action: PayloadAction<PendingTransactionData>) => {
            state.pendingTransactions = {
                ...state.pendingTransactions,
                ...{ [action.payload.transactionId]: action.payload },
            }
        },
        removePendingTransaction: (state, action: PayloadAction<string>) => {
            delete state.pendingTransactions[action.payload]
        },
        updatePendingTransaction: (
            state,
            action: PayloadAction<Partial<PendingTransactionData> & { transactionId: string }>,
        ) => {
            let found = state.pendingTransactions[action.payload.transactionId]
            state.pendingTransactions[action.payload.transactionId] = {
                ...found,
                ...action.payload,
            }
        },
        filterOldTransactions: state => {
            const filteredTransactions: { [key: string]: PendingTransactionData } = {}
            Object.entries(state.pendingTransactions).forEach(([key, transaction]) => {
                const isLessThan15Mins =
                    Date.now() - new Date(transaction.createdAt)?.getTime() <
                    PENDING_TRANSACTION_VALIDITY_PERIOD * 60 * 1000
                if (isLessThan15Mins && transaction.status.toLowerCase() === 'pending') {
                    filteredTransactions[key] = transaction
                }
            })
            state.pendingTransactions = filteredTransactions
        },
        decrementCountdown: (state, action: PayloadAction<string>) => {
            const transactionId = action.payload
            if (state.pendingTransactions[transactionId] && state.pendingTransactions[transactionId].countdown > 0) {
                state.pendingTransactions[transactionId].countdown -= 1
            }
        },
        resetState: () => initialState,
    },
})

export const transactionsActions = transactionsSlice.actions
export type TransactionsActions = typeof transactionsSlice.actions
export default transactionsSlice.reducer
